import { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Coins, Scales, Buildings, SignOut, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const ICON_MAP = { coins: Coins, scale: Scales };

export default function DepartmentSelector() {
  const { user, logout, setCurrentDepartment, currentDepartment } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/departments').then(({ data }) => setDepts(data)).finally(() => setLoading(false));
  }, []);

  if (!user) return <Navigate to="/login" replace />;

  const pick = (d) => {
    setCurrentDepartment(d.code);
    if (d.code === 'legal') nav('/legal');
    else nav('/invoices');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100" dir="rtl">
      {/* Top bar */}
      <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-md flex items-center justify-center">
            <Buildings weight="bold" className="text-[#B4935A]" size={22} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold text-slate-900">منصة الإدارة التنفيذية</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Executive Management Suite</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-600 hidden md:block">
            مرحباً <span className="font-semibold text-slate-900">{user.name}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="dept-logout-btn">
            <SignOut className="ms-1" size={16} /> تسجيل الخروج
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-16">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#B4935A] mb-3">Select your workspace</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">اختر الإدارة التي تريد الدخول إليها</h1>
          <p className="text-slate-500 mt-4 text-base max-w-2xl mx-auto leading-loose">
            كل إدارة توفر بيئة عمل مستقلة بصلاحيات وأدوات مخصصة. يمكنك التبديل بينها من قائمة المستخدم في أي وقت.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-20">جارٍ تحميل الإدارات...</div>
        ) : depts.length === 0 ? (
          <div className="text-center text-slate-400 py-20">لا توجد إدارات مصرح لك بالدخول إليها. تواصل مع المسؤول.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {depts.map((d) => {
              const Icon = ICON_MAP[d.icon] || Buildings;
              const isActive = currentDepartment === d.code;
              return (
                <button
                  key={d.code}
                  onClick={() => pick(d)}
                  data-testid={`dept-card-${d.code}`}
                  className="group relative text-right bg-white border-2 border-slate-200 hover:border-slate-900 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                >
                  <div className="h-2" style={{ background: d.color }} />
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ background: `${d.color}15`, color: d.color }}
                      >
                        <Icon size={34} weight="duotone" />
                      </div>
                      {d.open_tasks > 0 && (
                        <div className="text-left">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">مهام مفتوحة</div>
                          <div className="text-2xl font-bold" style={{ color: d.color }}>{d.open_tasks}</div>
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{d.label_en}</div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-slate-900">{d.label_ar}</h2>
                    <p className="text-sm text-slate-500 leading-loose min-h-[3rem]">{d.description}</p>
                    <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: d.color }}>
                        {isActive ? 'العودة إلى هذه الإدارة' : 'الدخول إلى الإدارة'}
                      </span>
                      <ArrowLeft className="text-slate-400 group-hover:text-slate-900 transition-colors" size={20} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center text-xs text-slate-400 uppercase tracking-widest">
          v1.1 · نظام صلاحيات مستقل لكل إدارة
        </div>
      </main>
    </div>
  );
}
